import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    nav: {
      rankings: 'Rankings',
      news: 'Noticias',
      donation: 'Donaciones',
      download: 'Descargar',
      register: 'Registrarse',
      login: 'Login',
      logout: 'Cerrar Sesión',
      registerNow: 'Registrarse Ahora',
      myAccount: 'Mi Cuenta'
    }
  },
  en: {
    nav: {
      rankings: 'Rankings',
      news: 'News',
      donation: 'Donations',
      download: 'Download',
      register: 'Register',
      login: 'Login',
      logout: 'Log Out',
      registerNow: 'Register Now',
      myAccount: 'My Account'
    }
  },
  pt: {
    nav: {
      rankings: 'Rankings',
      news: 'Notícias',
      donation: 'Doações',
      download: 'Baixar',
      register: 'Registrar',
      login: 'Login',
      logout: 'Sair',
      registerNow: 'Registrar Agora',
      myAccount: 'Minha Conta'
    }
  }
};
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState<Language>('es');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isLoggedIn,
    logout
  } = useAuth();
  const t = translations[language].nav;
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({
          behavior: 'smooth'
        });
      }, 300);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const navLinks = [{
    name: t.news,
    href: '#news'
  }, {
    name: t.rankings,
    href: '#rankings'
  }, {
    name: t.donation,
    href: '/donation'
  }, {
    name: t.download,
    href: '#download'
  }];
  const languages = [{
    code: 'es' as Language,
    name: 'Español',
    flag: '🇪🇸'
  }, {
    code: 'en' as Language,
    name: 'English',
    flag: '🇺🇸'
  }, {
    code: 'pt' as Language,
    name: 'Português',
    flag: '🇧🇷'
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Store language in localStorage and dispatch event
  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    setShowLangMenu(false);
    localStorage.setItem('language', lang);
    window.dispatchEvent(new CustomEvent('languageChange', {
      detail: lang
    }));
  };
  // Load language from localStorage on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang && ['es', 'en', 'pt'].includes(savedLang)) {
      setLanguage(savedLang);
    }
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-darkerBlue/90 backdrop-blur-md border-b border-white/5 py-3' : 'bg-transparent py-5'}`} data-id="element-186">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-187">
        <div className="flex justify-between items-center" data-id="element-188">
          {/* Logo */}
          <a href="/" onClick={handleLogoClick} className="flex items-center group" data-id="element-189">
            <img src="https://cdn.magicpatterns.com/uploads/te68Ekxjzq3VWFCodnkQT6/ChatGPT_Image_Mar_26,_2026,_01_29_43_AM.png" alt="MU Vanguard Logo" className="h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105" data-id="element-190" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-191">
            {navLinks.map(link => <a key={link.name} href={link.href} onClick={e => handleNavClick(e, link.href)} className={`font-cinzel text-sm font-semibold transition-colors relative group text-gray-300 hover:text-gold ${link.name === t.donation ? 'shimmer-text' : ''}`} data-id="element-192">
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gold transition-all duration-300 group-hover:w-full" data-id="element-193"></span>
              </a>)}

            <div className="flex items-center gap-4 ml-4" data-id="element-194">
              <a href="#register" onClick={e => handleNavClick(e, '#register')} className="btn-primary py-2 px-6 text-sm" data-id="element-195">
                {t.register}
              </a>
              {isLoggedIn ? <>
                  <a href="/dashboard" onClick={e => handleNavClick(e, '/dashboard')} className="btn-primary py-2 px-6 text-sm" data-id="element-196">
                    {t.myAccount}
                  </a>
                  <button onClick={() => {
                logout();
                navigate('/');
              }} className="flex items-center gap-2 text-red-400 hover:text-red-300 font-cinzel text-sm font-semibold transition-colors" data-id="element-197">
                    <LogOut className="w-4 h-4" data-id="element-198" />
                    {t.logout}
                  </button>
                </> : <a href="/dashboard" onClick={e => handleNavClick(e, '/dashboard')} className="btn-primary py-2 px-6 text-sm" data-id="element-199">
                  {t.login}
                </a>}
            </div>

            {/* Language Switcher (Moved after Login) */}
            <div className="relative" data-id="element-200">
              <button onClick={() => setShowLangMenu(!showLangMenu)} className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors font-cinzel text-sm font-semibold" data-id="element-201">
                <Globe className="w-4 h-4" data-id="element-202" />
                {languages.find(l => l.code === language)?.flag}
              </button>

              <AnimatePresence data-id="element-203">
                {showLangMenu && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="absolute right-0 mt-2 glass-panel border border-white/10 rounded-lg overflow-hidden min-w-[140px]" data-id="element-204">
                    {languages.map(lang => <button key={lang.code} onClick={() => changeLanguage(lang.code)} className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${language === lang.code ? 'bg-gold/20 text-gold' : 'text-gray-300 hover:bg-white/5 hover:text-white'}`} data-id="element-205">
                        <span data-id="element-206">{lang.flag}</span>
                        <span data-id="element-207">{lang.name}</span>
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden text-gray-300 hover:text-gold transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-208">
            {isMobileMenuOpen ? <X size={28} data-id="element-209" /> : <Menu size={28} data-id="element-210" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-211">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-darkerBlue/95 backdrop-blur-xl border-b border-white/10 overflow-hidden" data-id="element-212">
            <div className="px-4 pt-2 pb-6 flex flex-col gap-4" data-id="element-213">
              {navLinks.map(link => <a key={link.name} href={link.href} onClick={e => {
            handleNavClick(e, link.href);
            setIsMobileMenuOpen(false);
          }} className={`font-cinzel text-lg font-semibold transition-colors py-2 border-b border-white/5 text-gray-300 hover:text-gold ${link.name === t.donation ? 'shimmer-text' : ''}`} data-id="element-214">
                  {link.name}
                </a>)}

              {/* Mobile Language Switcher */}
              <div className="border-t border-white/5 pt-4" data-id="element-215">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-216">
                  <Globe className="w-3 h-3" data-id="element-217" /> Idioma / Language
                </p>
                <div className="flex gap-2" data-id="element-218">
                  {languages.map(lang => <button key={lang.code} onClick={() => {
                changeLanguage(lang.code);
                setIsMobileMenuOpen(false);
              }} className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${language === lang.code ? 'bg-gold text-darkerBlue' : 'bg-darkBlue/50 text-gray-300 hover:bg-darkBlue'}`} data-id="element-219">
                      {lang.flag} {lang.code.toUpperCase()}
                    </button>)}
                </div>
              </div>

              <div className="flex flex-col gap-3 mt-4" data-id="element-220">
                <a href="#register" onClick={e => {
              handleNavClick(e, '#register');
              setIsMobileMenuOpen(false);
            }} className="btn-primary text-center" data-id="element-221">
                  {t.registerNow}
                </a>
                {isLoggedIn ? <>
                    <a href="/dashboard" onClick={e => {
                handleNavClick(e, '/dashboard');
                setIsMobileMenuOpen(false);
              }} className="btn-primary text-center" data-id="element-222">
                      {t.myAccount}
                    </a>
                    <button onClick={() => {
                logout();
                navigate('/');
                setIsMobileMenuOpen(false);
              }} className="flex items-center justify-center gap-2 text-red-400 hover:text-red-300 font-cinzel text-sm font-semibold transition-colors py-2" data-id="element-223">
                      <LogOut className="w-4 h-4" data-id="element-224" />
                      {t.logout}
                    </button>
                  </> : <a href="/dashboard" onClick={e => {
              handleNavClick(e, '/dashboard');
              setIsMobileMenuOpen(false);
            }} className="btn-primary text-center" data-id="element-225">
                    {t.login}
                  </a>}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}